export enum TransferFormat {
	Text = 1,
	Binary = 2,
}

function noop(): void {
	// noop
}

interface AbstractTransport {
	onMessage: ((data: string | ArrayBuffer) => void) | null;
	onClose: ((error?: Error) => void) | null;
}

export interface WebSocketCtor<T extends WebSocket = WebSocket> {
	new (url: string, protocols?: string | string[], options?: unknown): T;
	readonly CLOSED: number;
	readonly CLOSING: number;
	readonly CONNECTING: number;
	readonly OPEN: number;
}

export class WebSocketTransport implements AbstractTransport {
	public onMessage: ((data: string | ArrayBuffer) => void) | null;
	public onClose: ((error?: Error) => void) | null;

	private readonly webSocketModuleProtocol: WebSocketCtor;
	private webSocket: WebSocket | null = null;

	/** @internal */
	public static create(webSocketModuleProtocol: WebSocketCtor): WebSocketTransport {
		return new WebSocketTransport(webSocketModuleProtocol);
	}

	private constructor(webSocketModuleProtocol: WebSocketCtor) {
		this.webSocketModuleProtocol = webSocketModuleProtocol;
		this.onMessage = null;
		this.onClose = null;
	}

	public async connect(url: string, transferFormat: TransferFormat): Promise<void> {
		const webSocket = new this.webSocketModuleProtocol(url);
		let opened = false;

		if (transferFormat === TransferFormat.Binary) {
			webSocket.binaryType = 'arraybuffer';
		}

		return new Promise((resolve, reject) => {
			webSocket.onopen = () => {
				opened = true;
				this.webSocket = webSocket;
				resolve();
			};

			webSocket.onmessage = (message: MessageEvent) => {
				if (this.onMessage) {
					try {
						this.onMessage(message.data);
						// biome-ignore lint/correctness/noUnusedVariables: suppressed due to migration - should be fixed
					} catch (error: unknown) {
						this.close();

						return;
					}
				}
			};

			webSocket.onerror = (event: Event) => {
				reject(event);
			};

			webSocket.onclose = (event: CloseEvent) => {
				if (opened) {
					this.closeInternal(event);

					return;
				}
			};
		});
	}

	public close(): Promise<void> {
		if (this.webSocket) {
			this.closeInternal(undefined);
		}

		return Promise.resolve();
	}

	public send(data: string | ArrayBufferLike | Blob | ArrayBufferView): void {
		this.webSocket?.send(data);
	}

	private closeInternal(event?: CloseEvent | Error): void {
		if (this.webSocket) {
			this.webSocket.onclose = noop;
			this.webSocket.onmessage = noop;
			this.webSocket.onerror = noop;
			this.webSocket.close();
			this.webSocket = null;
		}

		if (this.onClose) {
			if (this.isCloseEvent(event) && (event.wasClean === false || event.code !== 1000)) {
				this.onClose(new Error(`WebSocket closed with status code: ${event.code} (${event.reason}).`));
			} else if (event instanceof Error) {
				this.onClose(event);
			} else {
				this.onClose();
			}
		}
	}

	private isCloseEvent(event: CloseEvent | Error | undefined): event is CloseEvent {
		return event != null && !(event instanceof Error) && typeof event.wasClean === 'boolean' && typeof event.code === 'number';
	}
}
